// src/app/pages/admin/AdminEventoAsociacionVacantesWrapper.tsx
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageTitle } from '../../../_metronic/layout/core';
import BackButton from '../../components/BackButton/BackButton';
import styleScss from './styles.module.scss';
import {
	buscarVacantesAsociacion,
	getVacantesAsociadasEvento,
	asociarVacanteEvento,
	desasociarVacanteEvento,
} from '../../modules/apps/user-management/users-list/core/_requests';
import { LoadingBackdrop } from '../../components/LoadingSpinner/LoadingSpinner';
import {
	Alert,
	AlertTitle,
	Button,
	Paper,
	Snackbar,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
} from '@mui/material';
import { formatDateDisplay, getErrorMessage } from './EventosEmpleabilidad.helpers';

const EVENTOS_LIST_PATH = '/admin/gestion-eventos';

interface VacanteAsociacion {
	id: number;
	codigo_vacante: string;
	nombre_vacante: string;
	createdAt: string;
	fecha_aprobacion: string | null;
	fecha_limite_aplicacion: string;
	id_empresa: number;
	Empresa?: {
		razon_social: string;
		tipo_documento: string;
		numero_documento: string;
	};
}

interface AsociacionVacantesPageProps {
	embedded?: boolean;
	readOnly?: boolean;
}

const AsociacionVacantesPage = ({ embedded = false, readOnly = false }: AsociacionVacantesPageProps = {}) => {
	const { id: eventoId } = useParams();
	const [codigoVacante, setCodigoVacante] = useState('');
	const [vacantes, setVacantes] = useState<VacanteAsociacion[]>([]);
	const [asociadas, setAsociadas] = useState<number[]>([]);
	const [buscado, setBuscado] = useState(false);
	const [loading, setLoading] = useState(false);
	const [procesandoId, setProcesandoId] = useState<number | null>(null);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const [open, setOpen] = useState(false);
	const [alert, setAlert] = useState(true);
	const [alertMsg, setAlertMsg] = useState('');

	const notify = (msg: string, success = true) => {
		setAlert(success);
		setAlertMsg(msg);
		setOpen(true);
		setTimeout(() => setOpen(false), 3000);
	};

	const mapAsociacionToVacante = (asociacion: any): VacanteAsociacion => {
		const vaga = asociacion.EmpresaVaga || {};
		return {
			id: vaga.id,
			codigo_vacante: vaga.codigo_vacante,
			nombre_vacante: vaga.nombre_vacante,
			createdAt: vaga.createdAt,
			fecha_aprobacion: vaga.fecha_aprobacion,
			fecha_limite_aplicacion: vaga.fecha_limite_aplicacion,
			id_empresa: vaga.id_empresa,
			Empresa: vaga.Empresa,
		};
	};

	const cargarAsociadas = () => {
		if (!eventoId) return;
		setLoading(true);
		getVacantesAsociadasEvento(eventoId)
			.then((response: any) => {
				const asociaciones = response?.data || [];
				setAsociadas(asociaciones.map((asociacion: any) => asociacion.empresa_vaga_id));
				setVacantes(asociaciones.map(mapAsociacionToVacante));
				setBuscado(true);
				setPage(0);
			})
			.catch((error: any) => {
				notify(getErrorMessage(error), false);
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		cargarAsociadas();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [eventoId]);

	const handleBuscar = () => {
		if (!codigoVacante.trim()) {
			notify('Debe ingresar el código o el nombre de la vacante', false);
			return;
		}
		setLoading(true);
		buscarVacantesAsociacion(codigoVacante.trim())
			.then((response: any) => {
				setVacantes(response?.data || []);
				setBuscado(true);
				setPage(0);
			})
			.catch((error: any) => {
				notify(getErrorMessage(error), false);
			})
			.finally(() => setLoading(false));
	};

	const handleKeyDownBuscar = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Enter') handleBuscar();
	};

	const handleLimpiarFiltros = () => {
		setCodigoVacante('');
		cargarAsociadas();
	};

	const handleAsociar = (vacanteId: number) => {
		if (!eventoId) return;
		setProcesandoId(vacanteId);
		asociarVacanteEvento(eventoId, vacanteId)
			.then(() => {
				setAsociadas((prev) => [...prev, vacanteId]);
				notify('Vacante asociada al evento con éxito.');
				handleLimpiarFiltros();
			})
			.catch((error: any) => {
				notify(getErrorMessage(error), false);
			})
			.finally(() => setProcesandoId(null));
	};

	const handleDesasociar = (vacanteId: number) => {
		if (!eventoId) return;
		setProcesandoId(vacanteId);
		desasociarVacanteEvento(eventoId, vacanteId)
			.then(() => {
				setAsociadas((prev) => prev.filter((asociadaId) => asociadaId !== vacanteId));
				notify('Asociación eliminada con éxito.');
				handleLimpiarFiltros();
			})
			.catch((error: any) => {
				notify(getErrorMessage(error), false);
			})
			.finally(() => setProcesandoId(null));
	};

	return (
		<div className={embedded ? '' : 'p-4 shadow-4 rounded-3 mt-15'} style={embedded ? undefined : { backgroundColor: '#FFFF' }}>
			{!embedded && <LoadingBackdrop loading={loading} />}
			{embedded ? (
				<h3 className='mb-4'>Asociación de Vacantes</h3>
			) : (
				<div className='row mb-4' style={{ alignItems: 'center' }}>
					<div className='col-sm-12 d-flex align-items-center' style={{ gap: '15px' }}>
						<BackButton to={EVENTOS_LIST_PATH} />
						<h2 className='m-0'>Asociación de Vacantes</h2>
					</div>
				</div>
			)}

			<div className='input-group d-flex flex-wrap align-items-center' style={{ gap: '10px' }}>
				<div style={{ minWidth: 280 }}>
					<TextField
						fullWidth
						size='small'
						label='Ingrese el código o nombre de la vacante'
						value={codigoVacante}
						onChange={(e) => setCodigoVacante(e.target.value)}
						onKeyDown={handleKeyDownBuscar}
					/>
				</div>
				<Button variant='contained' onClick={handleBuscar}>
					Buscar
				</Button>
				<Button variant='outlined' disabled={!codigoVacante.trim()} onClick={handleLimpiarFiltros}>
					Limpiar filtros
				</Button>
			</div>

			<div className={`${styleScss['table-limit']}`} style={{ maxWidth: '100%', overflowWrap: 'break-word', overflowY: 'hidden' }}>
				<Stack padding={2}>
					<TableContainer component={Paper} style={{ overflowY: 'visible', overflowWrap: 'break-word' }}>
						<Table style={{ overflowY: 'visible' }}>
							<TableHead className={`${styleScss['th-custom']}`}>
								<TableRow>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Código vacante</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Nombre vacante</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Fecha de creación vacante</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Fecha límite de aplicación</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Tipo documento empresa</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Número documento</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Nombre empresa</h4></TableCell>
									<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Acciones</h4></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{vacantes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((vacante) => {
									const estaAsociada = asociadas.includes(vacante.id);
									const procesando = procesandoId === vacante.id;
									return (
										<TableRow key={vacante.id}>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{vacante.codigo_vacante}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{vacante.nombre_vacante}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{formatDateDisplay(vacante.createdAt)}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{formatDateDisplay(vacante.fecha_limite_aplicacion)}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{vacante.Empresa?.tipo_documento}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{vacante.Empresa?.numero_documento}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{vacante.Empresa?.razon_social}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
												<Stack direction='column' spacing={1} alignItems='center'>
													<Button
														variant='contained'
														size='small'
														disabled={estaAsociada || procesando || readOnly}
														onClick={() => handleAsociar(vacante.id)}
													>
														Agregar asociación
													</Button>
													<Button
														variant='outlined'
														color='error'
														size='small'
														disabled={!estaAsociada || procesando || readOnly}
														onClick={() => handleDesasociar(vacante.id)}
													>
														Eliminar asociación
													</Button>
												</Stack>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
							{!loading && buscado && vacantes.length === 0 && (
								<tfoot>
									<TableRow>
										<TableCell colSpan={8}>
											<p className='text-center' style={{ width: '100%', textAlign: 'center' }}>
												No se encontraron registros
											</p>
										</TableCell>
									</TableRow>
								</tfoot>
							)}
						</Table>
						<TablePagination
							rowsPerPageOptions={[10, 20, 50]}
							component='div'
							style={{ overflow: 'visible', fontFamily: 'Poppins', fontSize: '14.5px' }}
							count={vacantes.length}
							rowsPerPage={rowsPerPage}
							page={page}
							labelRowsPerPage='Filas por página'
							onPageChange={(_event, newPage) => setPage(newPage)}
							onRowsPerPageChange={(event) => {
								setRowsPerPage(parseInt(event.target.value, 10));
								setPage(0);
							}}
						/>
					</TableContainer>
				</Stack>
			</div>

			<Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={open} onClose={() => setOpen(false)}>
				<Alert onClose={() => setOpen(false)} variant='filled' severity={alert ? 'success' : 'error'} sx={{ whiteSpace: 'pre-line' }}>
					{alert ? <AlertTitle>Éxito</AlertTitle> : <AlertTitle>Error</AlertTitle>}
					{alertMsg}
				</Alert>
			</Snackbar>
		</div>
	);
};

const AdminEventoAsociacionVacantesWrapper = () => {
	return (
		<>
			<PageTitle breadcrumbs={[]}>Asociación de Vacantes</PageTitle>
			<AsociacionVacantesPage />
		</>
	);
};

export { AdminEventoAsociacionVacantesWrapper, AsociacionVacantesPage };
